import { useState } from 'react'
import {
  ago, fmtTime, nodeName, retireCompute, transitionMission,
  type FleetNode, type Me, type Mission,
} from '../api'
import { useT } from '../lang'
import { invalidate, useApi } from '../useApi'
import { armListen } from '../listenIntent'
import { Link, navigate } from '../router'
import { PageHead } from '../components/Shell'
import { Alive, ContactBadge, Empty, ErrorBox, Rows, contact } from '../components/bits'
import { Menu, type MenuAction } from '../components/Menu'

/** The landing page: one card per deployed hydrophone, listening one click away.
 *
 *  This was a table called "Fleet" and it was wrong twice over. "Fleet" is a
 *  word for a hundred units, not for the one or two anybody actually has, and
 *  it sat next to a nav item called "Hydrophones" that meant something else
 *  entirely. Worse, the thing people open this application to do -- listen --
 *  was three clicks behind a table row with no affordance suggesting you should
 *  click it.
 *
 *  Cards rather than rows because at this scale a table is all chrome and no
 *  content, and because a card has somewhere obvious to put a button.
 */
export function Fleet({ me }: { me: Me }) {
  const t = useT()
  // Retired units are off by default. Every box ever enrolled used to keep a
  // card here for ever, including every throwaway from commissioning, so the
  // one hydrophone actually in the water was outnumbered by tests on the page
  // whose entire job is to answer "what have I got in the water".
  const [showRetired, setShowRetired] = useState(false)
  const q = useApi<{ items: FleetNode[]; retired_total: number }>(
    `/v1/fleet${showRetired ? '?include_retired=true' : ''}`,
    { poll: 20000 },
  )
  const nodes = q.data?.items ?? []
  const retiredTotal = q.data?.retired_total ?? 0

  return (
    <>
      <PageHead
        title={t('Listening posts')}
        cta={
          me.can_write && (
            /* One way in, and the choice of what you are adding is the first
               question rather than a decision encoded in which button you
               pressed. Two buttons made you choose between "hydrophone" and
               "recorder" before anything had explained that the difference is
               whether it has an uplink. */
            /* An SVG rather than a "+" character. Flex centring centres the
               line box, not the glyph's ink, and where the ink sits inside
               that box is a property of the font -- so a typed plus lands
               visibly off-centre and moves if the face ever changes. Two
               strokes on a 24-box are centred by construction, and it matches
               every other icon here. */
            <Link className="btn primary addbtn" to="/add"
              aria-label="Add hardware" title="Add hardware">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </Link>
          )
        }
      />

      <ErrorBox error={q.error} />

      {q.cold ? (
        <div className="card"><Rows /></div>
      ) : nodes.length === 0 ? (
        <div className="card flush">
          <Empty
            title="Nothing deployed yet"
            action={
              me.can_write ? (
                // Same destination as the header's call to action, so it is
                // not a second one. Two accents pointing at one page is the
                // duplication the rule exists to stop.
                <Link className="btn" to="/add">
                  Add your first hydrophone
                </Link>
              ) : undefined
            }
          >
            A node with an uplink gives you a short code to write on the box and
            appears here by itself. A recorder is registered here and its card
            imported after you collect it.
          </Empty>
        </div>
      ) : (
        <div className="units">
          {nodes.map((n) => (
            <Unit key={n.compute_id} n={n} canWrite={me.can_write}
              onChanged={() => { invalidate('/v1/fleet'); q.reload() }} />
          ))}
        </div>
      )}

      {(retiredTotal > 0 || showRetired) && (
        <button className="btn ghost sm" style={{ marginTop: 14 }}
          onClick={() => setShowRetired(!showRetired)}>
          {showRetired
            ? 'Show only what is in service'
            : `Show ${retiredTotal} retired unit${retiredTotal === 1 ? '' : 's'}`}
        </button>
      )}

      <Recorders canWrite={me.can_write} />
    </>
  )
}

/** Units that record without a compute.
 *
 *  A separate section rather than more cards in the grid above, because
 *  /v1/fleet is `FROM computes` and structurally cannot return one. Unioning
 *  the two shapes into a single list would mean a card that has to hide half
 *  its own controls — there is no uplink to be alive on, no live stream to
 *  open, and no desired state to set.
 */
function Recorders({ canWrite }: { canWrite: boolean }) {
  // Paged rather than fetched whole. Five units today is not the design point:
  // a survey with forty recorders across a season is the same query, and a
  // list that silently truncates reads as "that is all of them".
  const [limit, setLimit] = useState(12)
  const q = useApi<{ total: number; items: Mission[] }>(
    `/v1/missions?recorder=true&limit=${limit}`,
  )
  const items = q.data?.items ?? []
  const total = q.data?.total ?? 0

  // Absent rather than empty. Someone with no recorders should not carry a
  // permanent empty box for a kind of hardware they do not own.
  if (!q.cold && total === 0) return null

  return (
    <section style={{ marginTop: 34 }}>
      <h2 className="display" style={{ fontSize: 19, marginBottom: 4 }}>
        Recorders
      </h2>
      <p className="hint" style={{ marginBottom: 14 }}>
        No uplink. Recordings arrive by importing the card after recovery.
      </p>

      <ErrorBox error={q.error} />

      {q.cold ? (
        <div className="card"><Rows /></div>
      ) : (
        <div className="units">
          {items.map((m) => (
            <RecorderUnit key={m.mission_id} m={m} canWrite={canWrite} />
          ))}
        </div>
      )}

      {total > items.length && (
        <button className="btn ghost sm" style={{ marginTop: 14 }}
          onClick={() => setLimit(limit + 24)}>
          Show more — {items.length} of {total}
        </button>
      )}
    </section>
  )
}

function RecorderUnit({ m, canWrite }: { m: Mission; canWrite: boolean }) {
  const where = m.site_name ?? m.site_id ?? 'No site'
  const name = m.microphone_label ?? m.microphone_serial ?? m.microphone_id ?? '—'
  const open = !m.ended_at

  const actions: MenuAction[] = [
    { label: 'Open deployment', onSelect: () => navigate(`/missions/${encodeURIComponent(m.mission_id)}`) },
  ]
  if (canWrite && m.microphone_id) {
    actions.push({
      label: 'Unit and calibration',
      onSelect: () => navigate(`/calibration/${encodeURIComponent(m.microphone_id!)}`),
    })
  }

  return (
    /* No `is-alive` state and no pulse. There is no uplink, so "last heard"
       is not a question that has an answer -- showing one would be a liveness
       claim about a device sitting on the seabed with no radio. */
    <article className="unit">
      <div className="unit-top">
        <h2 className="place display">{where}</h2>
        <span className="badge">{m.state}</span>
        {m.purpose === 'test' && <span className="badge">test</span>}
        <Menu actions={actions} label={`Actions for ${name} at ${where}`} />
      </div>

      <div className="unit-name">{name}</div>

      <dl className="unit-meta">
        <div>
          <dt>Model</dt>
          <dd>{m.recorder_model ?? '—'}</dd>
        </div>
        <div>
          <dt>{open ? 'Deployed' : 'Window'}</dt>
          <dd className="num" title={`${fmtTime(m.started_at)} — ${m.ended_at ? fmtTime(m.ended_at) : 'still out'}`}>
            {open ? ago(m.started_at) : fmtTime(m.started_at)}
          </dd>
        </div>
        {m.deploy_depth_m !== null && m.deploy_depth_m !== undefined && (
          <div>
            <dt>Depth</dt>
            <dd className="num">{m.deploy_depth_m} m</dd>
          </div>
        )}
      </dl>

      <div className="unit-actions">
        <Link className="btn" to={`/missions/${encodeURIComponent(m.mission_id)}/recordings`}>
          Recordings
        </Link>
        <Link className="btn ghost" to={`/missions/${encodeURIComponent(m.mission_id)}`}>
          Details
        </Link>
      </div>
    </article>
  )
}

function Unit({
  n, canWrite, onChanged,
}: { n: FleetNode; canWrite: boolean; onChanged: () => void }) {
  const deployed = !!n.mission_id
  const retired = !!n.revoked_at
  const where = n.site_name ?? n.site_id ?? 'Not deployed'
  const lastSeen = n.last_seen_at ?? n.last_upload_at
  const state = contact(lastSeen)
  const live = state === 'receiving'
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setErr(null)
    try {
      await fn()
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const actions: MenuAction[] = []
  if (deployed) {
    actions.push({ label: 'Open deployment', onSelect: () => navigate(`/missions/${encodeURIComponent(n.mission_id!)}`) })
  }
  actions.push({ label: 'Unit details', onSelect: () => navigate(`/nodes/${encodeURIComponent(n.compute_id)}`) })

  if (canWrite && deployed) {
    actions.push({
      label: 'Bring it in',
      confirm: 'Bring it in — confirm',
      detail: 'Records that the hydrophone came out of the water. The deployment closes and keeps its recordings. The unit stays in this list.',
      onSelect: () => void run(() => transitionMission(n.mission_id!, 'recovered')),
    })
  }
  if (canWrite) {
    /* One removal action, not two.
     *
     * There used to be a "This was only a test" here as well, which archived
     * the deployment and nothing else. On a card that represents a physical
     * unit that reads as "remove this whole thing", but the card's visibility
     * is governed by the unit, so the deployment closed and the card stayed --
     * now labelled "Not deployed", which looks exactly like the action did
     * nothing. Retiring already archives any open deployment, so the two were
     * one action wearing two labels and the wrong one was easier to reach. */
    actions.push(retired
      ? {
          label: 'Put back in service',
          onSelect: () => void run(() => retireCompute(n.compute_id, false)),
        }
      : {
          label: 'Retire — it was only a test',
          confirm: 'Retire it — confirm',
          detail: 'Removes it from this list and stops it uploading. Any open deployment is archived with it. Nothing is deleted, and putting it back is one click.',
          danger: true,
          onSelect: () => void run(() => retireCompute(n.compute_id, true)),
        })
  }

  return (
    <article className={`unit${live ? ' is-alive' : ''}${retired ? ' is-retired' : ''}`}>
      <div className="unit-top">
        <h2 className="place display">{where}</h2>
        {retired && <span className="badge">retired</span>}
        <Menu actions={actions} busy={busy}
          label={`Actions for ${nodeName(n)} at ${where}`} />
        {/* Driven by data arriving, never by mission state. A mission stays
            `active` for a unit that was unplugged a week ago, and pulsing on
            that would be a liveness claim nothing here can support. */}
        {live && <Alive label={`${where} is receiving now`} />}
        {/* And the other way round, which used to be nothing at all. Retired
            units are excluded: they are meant to be off, so labelling them
            offline is noise on the one screen that should carry none. */}
        {!live && !retired && <ContactBadge state={state} at={lastSeen} />}
      </div>

      {/* Only a name a person chose. nodeName() falls back to the compute_id,
          and "cmp-5e8d9f7b" under a place name is noise -- it identifies the
          row to the database, not the box to the operator. The way into the
          unit is "Unit details" in the menu, which is where the identifier and
          everything else about the hardware now lives. */}
      {n.display_name && <div className="unit-name">{n.display_name}</div>}

      <dl className="unit-meta">
        <div>
          <dt>{live ? 'Listening' : state === 'never' ? 'Contact' : 'Last heard'}</dt>
          {/* Still not "online". There is no broker and no node state table, so
              nothing here can tell a powered-off unit from one with a dead
              uplink. The pulse says "data is arriving right now", which is a
              smaller and true claim; the age says the rest. */}
          <dd className="num" title={`Last contact: ${fmtTime(lastSeen)}`}>
            {live ? 'now' : ago(lastSeen)}
          </dd>
        </div>
        {/* A move that was arranged last week and a move that was quietly
            cancelled look identical until the day itself, which is much too
            late to find out. This is the screen someone glances at to check
            the plan is still standing, so the plan belongs on it. */}
        {n.pending && !retired && (
          <div>
            <dt>Moving to</dt>
            <dd title={n.pending.activate_after
              ? `Moves the first time it checks in after ${fmtTime(n.pending.activate_after)}.`
              : 'Moves the next time it checks in.'}>
              {n.pending.site_name ?? 'a new site'}
              {n.pending.activate_after && (
                <span className="faint"> · {fmtTime(n.pending.activate_after)}</span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {err && <div className="notice bad" style={{ marginBottom: 10 }}>{err}</div>}

      <div className="unit-actions">
        {retired ? (
          <Link className="btn ghost" to={`/nodes/${encodeURIComponent(n.compute_id)}`}>
            Manage
          </Link>
        ) : deployed ? (
          <>
            {/* Secondary on purpose. This is a per-row action, so with a
                fleet of twenty it would put twenty accents on one screen and
                the page would have no call to action at all -- only noise. */}
            <Link className="btn" onClick={armListen}
              to={`/missions/${encodeURIComponent(n.mission_id!)}/listen`}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
                <path d="M4 13h3v6H5.5A1.5 1.5 0 0 1 4 17.5V13Zm16 0h-3v6h1.5a1.5 1.5 0 0 0 1.5-1.5V13Z" />
              </svg>
              Listen
            </Link>
            <Link className="btn ghost" to={`/missions/${encodeURIComponent(n.mission_id!)}`}>
              Details
            </Link>
          </>
        ) : (
          <Link className="btn" to={`/nodes/${encodeURIComponent(n.compute_id)}`}>
            Set up
          </Link>
        )}
      </div>
    </article>
  )
}
